import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  useCreateMedicalTextAssociations,
  useMedicalTextAssociations,
  useUpdateMedicalTextAssociations,
} from "@/hooks/useMedicalTextAssociations";
import { supabase } from "@/integrations/supabase/client";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { IssuesPanel } from "./issues/IssuesPanel";
import { useMedicalTextCodes } from "./useMedicalTextCodes";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { ComorbiditiesSection } from "./ComorbiditiesSection";
import { DiagnosesSectionLegacy } from "./DiagnosesSection";
import { MedicalTextInput } from "./MedicalTextInput";
import { PatientEncounterSection } from "./PatientEncounterSection";
import { ProceduresSection } from "./ProceduresSection";
import { TestTypeSelector } from "./TestTypeSelector";

interface NewMedicalTextDialogProps {
  onClose: () => void;
  open: boolean;
  medicalTextId?: string;
}

export function NewMedicalTextDialog({ onClose, open, medicalTextId }: NewMedicalTextDialogProps) {
  const [medicalExample, setMedicalText] = useState<{
    text: string;
    test_type: "Endoscopy" | "Histology" | "GP Summary" | "Referral" | null;
    notes: string;
    label: string;
    sequence_number: number | null;
  }>({
    text: "",
    test_type: null,
    notes: "",
    label: "",
    sequence_number: null,
  });

  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [selectedPatientId, setSelectedPatientId] = useState<string>("");
  const [selectedEncounterId, setSelectedEncounterId] = useState<string>("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Keyboard shortcut for full-screen toggle
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "F11" && open) {
        event.preventDefault();
        setIsFullScreen(!isFullScreen);
      }
      // Exit full-screen mode when Escape is pressed
      if (event.key === "Escape" && open && isFullScreen) {
        event.preventDefault();
        setIsFullScreen(false);
      }
    };

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, isFullScreen]);

  const {
    selectedProcedures,
    selectedDiagnoses,
    selectedCoMorbidities,
    handleProceduresChange,
    handleDiagnosisCodeSelectionChange,
    toggleOptionalStatus,
    isFetching: isFetchingMedicalTextCodes,
  } = useMedicalTextCodes(medicalTextId);

  const { data: associations, isFetching: isFetchingAssociations } = useMedicalTextAssociations(medicalTextId);
  const createAssociations = useCreateMedicalTextAssociations();
  const updateAssociations = useUpdateMedicalTextAssociations();

  // Initialize selections from associations exactly once per open cycle
  const initializedFromAssociationsRef = useRef(false);
  useEffect(() => {
    if (initializedFromAssociationsRef.current) return;
    if (isFetchingAssociations) return;
    if (!associations) return;

    initializedFromAssociationsRef.current = true;

    const nextCustomerId = associations.customer?.id || "";
    const nextPatientId = associations.patient?.id || "";
    const nextEncounterId = associations.encounter?.id || "";

    setSelectedCustomerId(nextCustomerId);
    setSelectedPatientId(nextPatientId);
    setSelectedEncounterId(nextEncounterId);
  }, [associations, isFetchingAssociations]);

  // Debounced autosave function for notes
  const debouncedSaveNotes = useCallback(
    debounce(async (notes: string, id?: string) => {
      if (!id) return;

      try {
        console.log("Autosaving notes:", notes);
        const { error } = await supabase.from("medical_text_examples").update({ notes }).eq("id", id);

        if (error) {
          console.error("Error autosaving notes:", error);
          toast.error("Failed to save notes automatically");
        } else {
          console.log("Notes autosaved successfully");
        }
      } catch (error) {
        console.error("Error in autosave:", error);
      }
    }, 1000),
    [toast],
  );

  useEffect(() => {
    const fetchMedicalText = async () => {
      if (!medicalTextId) return;

      try {
        const { data, error } = await supabase
          .from("medical_text_examples")
          .select("text, test_type, notes, label, sequence_number")
          .eq("id", medicalTextId)
          .single();

        if (error) throw error;
        if (data) {
          setMedicalText({
            text: data.text || "",
            test_type: data.test_type as "Endoscopy" | "Histology" | "GP Summary",
            notes: data.notes || "",
            label: data.label || "",
            sequence_number: data.sequence_number ?? null,
          });
        }
      } catch (error) {
        console.error("Error fetching medical text:", error);
        toast.error("Failed to load medical text example");
      }
    };

    fetchMedicalText();
  }, [medicalTextId]);

  const handleFormChange = () => {
    setHasUnsavedChanges(true);
  };

  const handleNotesChange = (newNotes: string) => {
    setMedicalText((existing) => ({
      ...existing,
      notes: newNotes,
    }));
    handleFormChange();

    // Trigger autosave for existing medical text
    if (medicalTextId) {
      debouncedSaveNotes(newNotes, medicalTextId);
    }
  };

  const autoSaveChanges = async () => {
    if (!hasUnsavedChanges || isSubmitting) return;

    // For GP Summary, only validate essential fields (text and test_type not required)
    const isGPSummary = medicalExample.test_type === "GP Summary";

    if (!isGPSummary) {
      if (!medicalExample.text.trim()) {
        toast.error("Please enter medical text");
      }

      if (!medicalExample.test_type) {
        toast.error("Please select a test type");
      }

      if (selectedProcedures.length === 0 && selectedDiagnoses.length === 0 && selectedCoMorbidities.length === 0) {
        toast.error("Please select at least one code");
      }
    }

    // For non-GP Summary, require the essential fields
    if (!isGPSummary && (!medicalExample.text.trim() || !medicalExample.test_type)) {
      onClose();
      return;
    }

    setIsSubmitting(true);

    try {
      let thisMedicalTextId = medicalTextId;

      let deletePromises: (() => Promise<unknown>)[] = [];
      if (medicalTextId) {
        const { error: updateError } = await supabase
          .from("medical_text_examples")
          .update({
            text: medicalExample.text,
            test_type: medicalExample.test_type,
            notes: medicalExample.notes,
            label: medicalExample.label,
          })
          .eq("id", thisMedicalTextId!);

        if (updateError) throw updateError;

        // get all ids for current expected procedures, diagnoses, and co-morbidities
        // update their order_index to avoid clash on unique key
        // create delete statements to be used later after new records are created
        const { data: existingProcedures } = await supabase
          .from("expected_procedures")
          .select("id")
          .eq("medical_text_example_id", thisMedicalTextId!);
        await Promise.all(
          (existingProcedures ?? []).map((p, idx) => {
            return supabase
              .from("expected_procedures")
              .update({
                order_index: idx + 1000,
              })
              .eq("id", p.id);
          }),
        );

        const { data: existingDiagnoses } = await supabase
          .from("expected_diagnosis")
          .select("id")
          .eq("medical_text_example_id", thisMedicalTextId!);
        await Promise.all(
          (existingDiagnoses ?? []).map((d, idx) => {
            return supabase
              .from("expected_diagnosis")
              .update({
                order_index: idx + 1000,
              })
              .eq("id", d.id);
          }),
        );

        const { data: existingCoMorbidities } = await supabase
          .from("expected_co_morbidities")
          .select("id")
          .eq("medical_text_example_id", thisMedicalTextId!)
          .order("order_index", { ascending: true });

        deletePromises = [
          async () =>
            await supabase
              .from("expected_procedures")
              .delete()
              .in(
                "id",
                (existingProcedures ?? []).map((p) => p.id),
              ),
          async () =>
            await supabase
              .from("expected_diagnosis")
              .delete()
              .in(
                "id",
                (existingDiagnoses ?? []).map((d) => d.id),
              ),
          async () =>
            await supabase
              .from("expected_co_morbidities")
              .delete()
              .in(
                "id",
                (existingCoMorbidities ?? []).map((c) => c.id),
              ),
        ];
      } else {
        const newId = crypto.randomUUID();

        const { error: medicalTextError } = await supabase.from("medical_text_examples").insert({
          id: newId,
          text: medicalExample.text,
          test_type: medicalExample.test_type,
          notes: medicalExample.notes,
          label: medicalExample.label,
        });

        if (medicalTextError) throw medicalTextError;
        thisMedicalTextId = newId;
      }

      // Only save procedures and diagnoses for non-GP Summary types
      if (!isGPSummary) {
        if (selectedProcedures.length > 0) {
          const procedureRecords = selectedProcedures.map((code, idx) => ({
            id: crypto.randomUUID(),
            medical_text_example_id: thisMedicalTextId!,
            code_id: code.id,
            order_index: idx,
          }));
          const { error: proceduresError } = await supabase.from("expected_procedures").insert(procedureRecords);
          if (proceduresError) throw proceduresError;
        }

        if (selectedDiagnoses.length > 0) {
          const diagnosisRecords = selectedDiagnoses.map((code, idx) => ({
            id: crypto.randomUUID(),
            medical_text_example_id: thisMedicalTextId!,
            code_id: code.icd_10_code.id,
            order_index: idx,
            is_optional: code.is_optional,
          }));
          const { error: diagnosesError } = await supabase.from("expected_diagnosis").insert(diagnosisRecords);
          if (diagnosesError) throw diagnosesError;
        }
      }

      // Always save co-morbidities (available for all types including GP Summary)
      if (selectedCoMorbidities.length > 0) {
        const coMorbidityRecords = selectedCoMorbidities.map((code, idx) => ({
          id: crypto.randomUUID(),
          medical_text_example_id: thisMedicalTextId!,
          code_id: code.icd_10_code.id,
          is_optional: code.is_optional,
          order_index: idx,
        }));

        const { error: coMorbiditiesError } = await supabase.from("expected_co_morbidities").insert(coMorbidityRecords);

        if (coMorbiditiesError) throw coMorbiditiesError;
      }

      if (deletePromises.length) {
        await Promise.allSettled(deletePromises.map((p) => p()));
      }

      // Handle patient and encounter associations
      if (medicalTextId) {
        // Update associations for existing medical text
        await updateAssociations.mutateAsync({
          patient_id: selectedPatientId || undefined,
          patient_encounter_id: selectedEncounterId || undefined,
          medical_text_example_id: thisMedicalTextId!,
        });
      } else {
        // Create associations for new medical text
        await createAssociations.mutateAsync({
          patient_id: selectedPatientId || undefined,
          patient_encounter_id: selectedEncounterId || undefined,
          medical_text_example_id: thisMedicalTextId!,
        });
      }

      toast.success(`Medical text example ${medicalTextId ? "updated" : "added"} successfully`);

      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error auto-saving medical text:", error);
      toast.error("Failed to auto-save changes");
    } finally {
      setIsSubmitting(false);
      onClose();
    }
  };

  const handleClose = async () => {
    if (hasUnsavedChanges && !isSubmitting) {
      await autoSaveChanges();
    } else {
      onClose();
    }
  };

  // Delete logic for medical text example
  const handleDelete = async () => {
    if (!medicalTextId) return;
    setIsSubmitting(true);
    try {
      // Delete the medical text example itself and expect CASCADE to delete all associated data
      const { error } = await supabase.from("medical_text_examples").delete().eq("id", medicalTextId);
      if (error) throw error;
      toast.success("Medical text example deleted successfully");
      setShowDeleteConfirm(false);
      setIsSubmitting(false);
      onClose();
    } catch (error) {
      console.error("Error deleting medical text example:", error);
      toast.error("Failed to delete medical text example");
      setIsSubmitting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (isFetchingMedicalTextCodes || isFetchingAssociations) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Loading Medical Text</DialogTitle>
            <DialogDescription>Please wait while we load the medical text data.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center h-[400px]">Loading...</div>
        </DialogContent>
      </Dialog>
    );
  }

  const isGPSummary = medicalExample.test_type === "GP Summary";

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          className={`${
            isFullScreen
              ? "w-screen h-screen max-w-none max-h-none rounded-none bg-background/95 backdrop-blur-sm"
              : "max-w-4xl max-h-[90vh]"
          } overflow-y-auto transition-all duration-300`}
        >
          <DialogHeader className="flex items-center justify-between">
            <DialogTitle>{medicalTextId ? "Edit Medical Text Example" : "Add New Medical Text Example"}</DialogTitle>
            <DialogDescription>
              {medicalTextId
                ? "Edit the medical text example and its associated codes."
                : "Create a new medical text example with associated codes."}
            </DialogDescription>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={() => setIsFullScreen(!isFullScreen)} className="ml-auto">
                    {isFullScreen ? (
                      <>
                        <Minimize2 className="h-4 w-4 mr-2" />
                        Compact View
                      </>
                    ) : (
                      <>
                        <Maximize2 className="h-4 w-4 mr-2" />
                        Full Screen
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Toggle full-screen mode (F11)</p>
                  <p className="text-xs text-muted-foreground">Press Escape to exit full-screen</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </DialogHeader>

          <div className={`space-y-6 ${isFullScreen ? "px-4" : ""}`}>
            {/* Customer, Patient and Encounter Selection */}
            <PatientEncounterSection
              selectedCustomerId={selectedCustomerId}
              selectedPatientId={selectedPatientId}
              selectedEncounterId={selectedEncounterId}
              onCustomerChange={(customerId) => {
                setSelectedCustomerId(customerId);
                // Reset patient and encounter when customer changes
                setSelectedPatientId("");
                setSelectedEncounterId("");
                handleFormChange();
              }}
              onPatientChange={(patientId) => {
                setSelectedPatientId(patientId);
                setSelectedEncounterId(""); // Reset encounter when patient changes
                handleFormChange();
              }}
              onEncounterChange={(encounterId) => {
                setSelectedEncounterId(encounterId);
                handleFormChange();
              }}
            />

            {/* Test Type Selection */}
            <TestTypeSelector
              value={medicalExample.test_type}
              onChange={(value) => {
                setMedicalText((prev) => ({
                  ...prev,
                  test_type: value,
                }));
                handleFormChange();
              }}
              label={medicalExample.label}
              sequenceNumber={medicalExample.sequence_number}
              onLabelChange={(label) => {
                setMedicalText((existing) => ({
                  ...existing,
                  label,
                }));
                handleFormChange();
              }}
            />

            <MedicalTextInput
              text={medicalExample.text}
              notes={medicalExample.notes}
              onTextChange={(text) => {
                setMedicalText((existing) => ({
                  ...existing,
                  text,
                }));
                handleFormChange();
              }}
              onNotesChange={handleNotesChange}
              medicalTextId={medicalTextId}
              isFullScreen={isFullScreen}
            />

            {/* Issues & Task Log Section - Only show for existing medical text */}
            {medicalTextId && (
              <div className="border-t pt-6">
                <IssuesPanel medicalTextExampleId={medicalTextId} />
              </div>
            )}

            {/* Procedures Section - Hidden for GP Summary */}
            {!isGPSummary && (
              <ProceduresSection
                selectedProcedures={selectedProcedures}
                onProceduresChange={handleProceduresChange}
                onFormChange={handleFormChange}
              />
            )}

            {/* Diagnoses Section - Hidden for GP Summary */}
            {!isGPSummary && (
              <DiagnosesSectionLegacy
                selectedDiagnoses={selectedDiagnoses}
                onDiagnosesChange={(diagnoses) => handleDiagnosisCodeSelectionChange(diagnoses, "diagnoses")}
                onToggleOptional={(id) => {
                  toggleOptionalStatus(id, "diagnoses");
                  handleFormChange();
                }}
                onFormChange={handleFormChange}
              />
            )}

            {/* Co-morbidities Section - Always shown */}
            <ComorbiditiesSection
              selectedCoMorbidities={selectedCoMorbidities}
              onCoMorbiditiesChange={(comorbidities) =>
                handleDiagnosisCodeSelectionChange(comorbidities, "co-morbidities")
              }
              onToggleOptional={(id) => {
                toggleOptionalStatus(id, "co-morbidities");
                handleFormChange();
              }}
              onFormChange={handleFormChange}
            />
            {/* Delete Button - Only show when editing */}
            {medicalTextId && (
              <div className="flex justify-end pt-4">
                <Button
                  variant="destructive"
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Medical Text Example?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the medical text example and all its associated
              codes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={isSubmitting} onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
